import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Check } from "lucide-react";
import { Eyebrow, Reveal } from "../Reveal";
import { useSiteTexts } from "../../hooks/useSiteTexts";

const VILLA_IMG = "https://images.unsplash.com/photo-1730126680640-bb6c3549d775?crop=entropy&cs=srgb&fm=jpg&q=85";
const INTERIOR_IMG = "https://images.unsplash.com/photo-1638885930125-85350348d266?crop=entropy&cs=srgb&fm=jpg&q=85";

const ParallaxImage = ({ src, alt, testid }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className="relative h-[340px] overflow-hidden rounded-2xl sm:h-[460px]">
      <motion.img
        src={src}
        alt={alt}
        data-testid={testid}
        style={{ y }}
        className="absolute inset-0 h-[116%] w-full object-cover"
      />
    </div>
  );
};

const Block = ({ eyebrow, title, text, items, cta, to, img, imgAlt, reversed, testidCta, testidImg }) => (
  <div className={`grid items-center gap-12 lg:grid-cols-2 lg:gap-20 ${reversed ? "" : ""}`}>
    <Reveal className={reversed ? "lg:order-2" : ""}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="mt-4 font-serif text-2xl font-normal tracking-tight text-azur sm:text-3xl lg:text-4xl">
        {title}
      </h2>
      <p className="mt-5 max-w-lg text-base font-light leading-relaxed text-slate-600 sm:text-lg">{text}</p>
      <ul className="mt-8 space-y-3">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-sm font-light text-slate-700 sm:text-base">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/15">
              <Check size={12} className="text-gold" />
            </span>
            {item}
          </li>
        ))}
      </ul>
      <Link
        to={to}
        data-testid={testidCta}
        className="group mt-10 inline-flex items-center gap-2 rounded-full bg-azur px-7 py-3.5 text-sm font-medium text-sand transition-colors duration-300 hover:bg-terracotta"
      >
        {cta}
        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </Reveal>
    <Reveal delay={0.15} className={reversed ? "lg:order-1" : ""}>
      <ParallaxImage src={img} alt={imgAlt} testid={testidImg} />
    </Reveal>
  </div>
);

export default function SellBuy() {
  const t = useSiteTexts();
  return (
    <section data-testid="sell-buy-section" className="py-12 lg:py-20">
      <div className="mx-auto max-w-7xl space-y-28 px-4 sm:px-8 lg:px-16">
        <Block
          eyebrow="Vendre"
          title={t("sell_title", "Vous souhaitez vendre ? Confiez-nous votre projet.")}
          text={t("sell_text", "Votre bien mérite une présentation à sa hauteur. Présentation, visites, échanges avec les acquéreurs, négociation et coordination jusqu'au notaire : nous prenons tout en charge.")}
          items={[
            "Estimation précise de votre bien",
            "Stratégie de commercialisation personnalisée",
            "Mise en valeur professionnelle",
            "Accompagnement jusqu'à la signature",
          ]}
          cta="Demander une estimation"
          to="/vendre"
          img={VILLA_IMG}
          imgAlt="Villa provençale à vendre sur la Côte d'Azur"
          testidCta="sell-cta-estimation"
          testidImg="sell-section-image"
        />
        <Block
          reversed
          eyebrow="Acheter"
          title={t("buy_title", "Vous cherchez votre prochaine adresse ?")}
          text={t("buy_text", "Nous vous accompagnons dans la recherche du bien qui correspond réellement à votre projet, à vos critères et à votre mode de vie.")}
          items={[
            "Recherche personnalisée et sélection de biens",
            "Organisation des visites",
            "Négociation en votre faveur",
            "Accompagnement notarial",
            "Suivi jusqu'à la remise des clés",
          ]}
          cta="Parler de mon projet"
          to="/acheter"
          img={INTERIOR_IMG}
          imgAlt="Intérieur lumineux d'une propriété de prestige"
          testidCta="buy-cta-project"
          testidImg="buy-section-image"
        />
      </div>
    </section>
  );
}
